/**
 * AnnotationScatterPatchesBuilder.ts — generic GLB scatter inside
 * authored polygons.
 *
 * Driven by TerrainItem rows with `procedural_key === "scatter"`
 * (slug `scatter_patch`). Each row carries `properties.pack` selecting
 * a pack at `public/decorations/<pack>/index.json`. Per-item properties
 * (density, scale_jitter, cell_m) override pack `defaults`.
 *
 * Pack manifest format:
 *   {
 *     "defaults": {
 *       "cellM": 1.2,
 *       "density": 0.35,
 *       "scaleJitter": 0.35,
 *       "targetHeightM": 0.18,
 *       "sinkM": 0.0,
 *       "allowedSurfaces": ["water"],
 *       "alignY": "water" | "ground"
 *     },
 *     "files": ["foo.glb", "bar.glb", ...]
 *   }
 *
 * Placement: deterministic grid in the polygon AABB, accept cells
 * passing point-in-polygon AND (sampleTerrainSurface ∈ allowedSurfaces).
 * `alignY: "water"` pins Y to waterLevel (lilies, reeds); `"ground"`
 * pins to sampleTerrainHeight (rye, rocks, flowers).
 */

import { loadGlb, type GltfMeshAsset } from "../assets/GltfLoader";
import {
  sampleTerrainHeight,
  sampleTerrainSurface,
  type TerrainAsset,
  type TerrainSurface,
} from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";

const GROUP_PREFIX = "annotation.scatter";
const PACK_BASE = "/decorations/";

interface PackDefaults {
  cellM: number;
  density: number;
  scaleJitter: number;
  targetHeightM?: number;
  sinkM: number;
  allowedSurfaces: TerrainSurface[];
  alignY: "water" | "ground";
  /** Procedural vertical bob (m). Useful for lilies/reeds on water. */
  floatAmp: number;
}

interface PackManifest {
  defaults?: Partial<PackDefaults>;
  files?: string[];
}

interface Variant {
  meshId: string;
  modelMinY: number;
  modelHeight: number;
  modelCenterX: number;
  modelCenterZ: number;
}

interface ResolvedPack {
  variants: Variant[];
  defaults: PackDefaults;
}

interface Patch {
  ring: Array<[number, number]>;
  pack: string;
  cellM?: number;
  density?: number;
  scaleJitter?: number;
}

function trsY(
  s: number,
  yaw: number,
  tx: number,
  ty: number,
  tz: number,
): Mat4 {
  const c = Math.cos(yaw);
  const sn = Math.sin(yaw);
  // prettier-ignore
  return new Float32Array([
    c * s,  0, -sn * s, 0,
    0,      s, 0,       0,
    sn * s, 0, c * s,   0,
    tx,     ty, tz,     1,
  ]) as Mat4;
}

function hash2(x: number, z: number): number {
  const h = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return h - Math.floor(h);
}

function pointInPolygon(
  x: number,
  z: number,
  ring: ReadonlyArray<readonly [number, number]>,
): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, zi] = ring[i]!;
    const [xj, zj] = ring[j]!;
    const intersect =
      zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

const FALLBACK_DEFAULTS: PackDefaults = {
  cellM: 1.5,
  density: 0.5,
  scaleJitter: 0.3,
  sinkM: 0.0,
  allowedSurfaces: ["meadow"],
  alignY: "ground",
  floatAmp: 0,
};

export class AnnotationScatterPatchesBuilder {
  /** Cache of resolved packs (one fetch + GLB load per pack across all patches). */
  private readonly packs = new Map<string, Promise<ResolvedPack | null>>();

  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  async build(terrain: TerrainAsset, waterLevel: number): Promise<void> {
    const patches = extractScatterPatches(terrain);
    if (patches.length === 0) return;

    let totalPlaced = 0;
    for (const patch of patches) {
      const pack = await this.resolvePack(patch.pack);
      if (!pack || pack.variants.length === 0) continue;
      totalPlaced += this.scatterPatch(terrain, waterLevel, patch, pack);
    }
    if (totalPlaced > 0) {
      console.log(
        `[ScatterPatches] placed ${totalPlaced} instance(s) across ${patches.length} patch(es)`,
      );
    }
  }

  private resolvePack(slug: string): Promise<ResolvedPack | null> {
    const cached = this.packs.get(slug);
    if (cached) return cached;
    const promise = this.loadPack(slug);
    this.packs.set(slug, promise);
    return promise;
  }

  private async loadPack(slug: string): Promise<ResolvedPack | null> {
    const url = `${PACK_BASE}${slug}/index.json`;
    let manifest: PackManifest;
    try {
      const res = await fetch(url);
      if (!res.ok) {
        console.warn(
          `[ScatterPatches] pack "${slug}" manifest HTTP ${res.status} at ${url}`,
        );
        return null;
      }
      manifest = (await res.json()) as PackManifest;
    } catch (err) {
      console.warn(`[ScatterPatches] pack "${slug}" manifest load failed`, err);
      return null;
    }

    const defaults = mergeDefaults(FALLBACK_DEFAULTS, manifest.defaults);
    const files = Array.isArray(manifest.files) ? manifest.files : [];
    if (files.length === 0) {
      console.warn(
        `[ScatterPatches] pack "${slug}" has no files[] (drop .glb files in public/decorations/${slug}/ and add them to its index.json)`,
      );
      return { variants: [], defaults };
    }

    const variants: Variant[] = [];
    for (const file of files) {
      const meshId = `${GROUP_PREFIX}.${slug}/${file}`;
      try {
        const mesh = await loadGlb(`${PACK_BASE}${slug}/${file}`);
        this.staticMeshPass.addGroup(meshId, mesh, 0, {
          castsShadow: false,
          receivesShadow: true,
          floatAmp: defaults.floatAmp,
        });
        variants.push(makeVariant(meshId, mesh));
      } catch (err) {
        console.warn(
          `[ScatterPatches] pack "${slug}" failed to load ${file}`,
          err,
        );
      }
    }
    return { variants, defaults };
  }

  private scatterPatch(
    terrain: TerrainAsset,
    waterLevel: number,
    patch: Patch,
    pack: ResolvedPack,
  ): number {
    const cellM = clamp(patch.cellM ?? pack.defaults.cellM, 0.2, 12.0);
    const density = clamp(patch.density ?? pack.defaults.density, 0.0, 1.0);
    const jitter = clamp(
      patch.scaleJitter ?? pack.defaults.scaleJitter,
      0.0,
      1.0,
    );
    const allowed = new Set<TerrainSurface>(pack.defaults.allowedSurfaces);
    const targetHeightM = pack.defaults.targetHeightM;
    const sinkM = pack.defaults.sinkM;
    const alignY = pack.defaults.alignY;
    const ring = patch.ring;
    const variants = pack.variants;

    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const [x, z] of ring) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    if (!isFinite(minX) || maxX <= minX || maxZ <= minZ) return 0;

    const seed = hash2(minX * 0.123, minZ * 0.789);
    let placed = 0;
    for (let gz = minZ; gz <= maxZ; gz += cellM) {
      for (let gx = minX; gx <= maxX; gx += cellM) {
        const j1 = hash2(gx + seed, gz - seed);
        if (j1 >= density) continue;
        const j2 = hash2(gx * 1.31 - seed, gz * 0.71 + seed);
        const j3 = hash2(gx + 4.13, gz - 7.91);
        const j4 = hash2(gx - 9.27, gz + 2.55);
        const px = gx + (j2 - 0.5) * cellM * 0.9;
        const pz = gz + (j3 - 0.5) * cellM * 0.9;
        if (!pointInPolygon(px, pz, ring)) continue;
        if (
          allowed.size > 0 &&
          !allowed.has(sampleTerrainSurface(terrain, px, pz))
        ) {
          continue;
        }

        const v = variants[Math.floor(j4 * variants.length) % variants.length]!;
        const yaw = hash2(px * 3.7, pz * 5.1) * Math.PI * 2;
        const baseScale =
          targetHeightM != null && v.modelHeight > 1e-6
            ? targetHeightM / v.modelHeight
            : 1.0;
        const scale = baseScale * (1 - jitter * 0.5 + j1 * jitter);

        const groundY =
          alignY === "water"
            ? waterLevel
            : Math.max(sampleTerrainHeight(terrain, px, pz), 0.0);
        const ty = groundY - v.modelMinY * scale - sinkM;

        const cy = Math.cos(yaw);
        const sn = Math.sin(yaw);
        const ox = (v.modelCenterX * cy + v.modelCenterZ * sn) * scale;
        const oz = (-v.modelCenterX * sn + v.modelCenterZ * cy) * scale;

        this.staticMeshPass.addInstance(
          v.meshId,
          trsY(scale, yaw, px - ox, ty, pz - oz),
        );
        placed++;
      }
    }
    return placed;
  }
}

function extractScatterPatches(terrain: TerrainAsset): Patch[] {
  const out: Patch[] = [];
  for (const obj of terrain.worldObjects ?? []) {
    const o = obj as {
      type?: string;
      geometry?: { type?: string; points?: unknown };
      properties?: Record<string, unknown>;
    };
    if (o.type !== "scatter") continue;
    const geometry = o.geometry;
    if (
      !geometry ||
      geometry.type !== "polygon" ||
      !Array.isArray(geometry.points)
    ) {
      continue;
    }
    const ring: Array<[number, number]> = [];
    for (const p of geometry.points as unknown[]) {
      if (
        Array.isArray(p) &&
        p.length >= 2 &&
        typeof p[0] === "number" &&
        typeof p[1] === "number"
      ) {
        ring.push([p[0], p[1]]);
      }
    }
    if (ring.length < 3) continue;
    const props = (o.properties ?? {}) as Record<string, unknown>;
    const pack = typeof props.pack === "string" ? props.pack.trim() : "";
    if (!pack) {
      console.warn(
        "[ScatterPatches] scatter_patch polygon without properties.pack — skipping",
      );
      continue;
    }
    out.push({
      ring,
      pack,
      cellM: maybeNum(props.cell_m),
      density: maybeNum(props.density),
      scaleJitter: maybeNum(props.scale_jitter),
    });
  }
  return out;
}

function maybeNum(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function mergeDefaults(
  base: PackDefaults,
  override: Partial<PackDefaults> | undefined,
): PackDefaults {
  if (!override) return { ...base };
  return {
    cellM: override.cellM ?? base.cellM,
    density: override.density ?? base.density,
    scaleJitter: override.scaleJitter ?? base.scaleJitter,
    targetHeightM: override.targetHeightM ?? base.targetHeightM,
    sinkM: override.sinkM ?? base.sinkM,
    allowedSurfaces: override.allowedSurfaces ?? base.allowedSurfaces,
    alignY: override.alignY ?? base.alignY,
    floatAmp: override.floatAmp ?? base.floatAmp,
  };
}

function makeVariant(meshId: string, mesh: GltfMeshAsset): Variant {
  const min = mesh.bbox.min;
  const max = mesh.bbox.max;
  return {
    meshId,
    modelMinY: min[1],
    modelHeight: max[1] - min[1],
    modelCenterX: (min[0] + max[0]) * 0.5,
    modelCenterZ: (min[2] + max[2]) * 0.5,
  };
}
